import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { Send, Trash2, MessageSquare, Repeat, FileText, Users as UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import UserAvatar from './UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * TaskDetailDialog – shows a single task with description, assignees, recurrence badge,
 * and a live comment thread. Works with both regular tasks and virtual series instances.
 *
 * Props:
 *   task        – the task object (may have `instance_id` if it's a series instance)
 *   open        – dialog open state
 *   onOpenChange
 *   users       – list of {id,name,avatar_color,avatar_url}
 */
const TaskDetailDialog = ({ task, open, onOpenChange, users = [] }) => {
  const { getAuthHeader, user } = useAuth();
  const { t, i18n } = useTranslation();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const getLocale = () => (i18n.language === 'de' ? de : enUS);

  // The comment API accepts either origin id or "origin::week::day" instance id.
  const commentTaskId = task?.instance_id || task?.id;

  const fetchComments = useCallback(async () => {
    if (!commentTaskId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/tasks/plan/${commentTaskId}/comments`, { headers: getAuthHeader() });
      setComments(r.data || []);
    } catch (_) { setComments([]); }
    finally { setLoading(false); }
  }, [commentTaskId, getAuthHeader]);

  useEffect(() => {
    if (open) fetchComments();
  }, [open, fetchComments]);

  // Scroll to bottom on new comments
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [comments.length]);

  const handleSend = async () => {
    const v = text.trim();
    if (!v) return;
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/tasks/plan/${commentTaskId}/comments`,
        { text: v },
        { headers: getAuthHeader() }
      );
      setText('');
      fetchComments();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('taskDetail.commentError'));
    } finally { setSending(false); }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm(t('taskDetail.confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/tasks/plan/${commentTaskId}/comments/${commentId}`, { headers: getAuthHeader() });
      fetchComments();
    } catch (_) { toast.error(t('taskDetail.commentError')); }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!task) return null;

  const assigneeUsers = (task.assigned_to || []).map(uid =>
    users.find(u => u.id === uid) || { id: uid, name: (task.assigned_to_names || []).find(Boolean) || '?' }
  );
  const recurrenceLabel = task.recurrence_type && task.recurrence_type !== 'none'
    ? t(`recurrence.type.${task.recurrence_type}`)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0 gap-0" aria-describedby="task-detail-desc">
        <DialogHeader className="p-5 pb-3 border-b border-zinc-100">
          <DialogTitle className="text-lg pr-6">{task.title}</DialogTitle>
          <p id="task-detail-desc" className="sr-only">{task.title}</p>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            {recurrenceLabel && (
              <span className="text-[10px] uppercase tracking-wide font-bold bg-[#FEE2E2] text-[#D93025] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <Repeat className="w-3 h-3" /> {recurrenceLabel}
              </span>
            )}
            {assigneeUsers.length > 0 && (
              <div className="flex items-center gap-1">
                <UsersIcon className="w-3.5 h-3.5 text-zinc-400" />
                <div className="flex items-center -space-x-1.5">
                  {assigneeUsers.slice(0, 5).map(u => (
                    <UserAvatar key={u.id} user={u} size="xs" ring />
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogHeader>

        {task.description && (
          <div className="px-5 pt-3 border-b border-zinc-100">
            <div className="text-xs font-medium text-zinc-500 mb-1 flex items-center gap-1">
              <FileText className="w-3 h-3" /> {t('taskDetail.description')}
            </div>
            <p className="text-sm text-zinc-800 whitespace-pre-wrap mb-3">{task.description}</p>
          </div>
        )}

        <div className="px-5 pt-3 pb-2 flex items-center gap-2 text-xs font-medium text-zinc-500">
          <MessageSquare className="w-3.5 h-3.5" />
          {t('taskDetail.comments')} ({comments.length})
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto px-5 pb-4 space-y-3 min-h-[100px]" data-testid="task-comments-list">
          {loading && comments.length === 0 ? (
            <div className="text-center text-zinc-400 py-6 text-sm">{t('common.loading')}</div>
          ) : comments.length === 0 ? (
            <div className="text-center text-zinc-400 py-6 text-sm">{t('taskDetail.noComments')}</div>
          ) : comments.map(c => {
            const author = users.find(u => u.id === c.user_id) || {
              id: c.user_id,
              name: c.user_name,
              avatar_color: c.user_avatar_color,
              avatar_url: c.user_avatar_url,
            };
            const isMine = c.user_id === user?.id;
            return (
              <div key={c.id} className={`flex gap-2 ${isMine ? 'flex-row-reverse' : ''}`} data-testid={`comment-${c.id}`}>
                <UserAvatar user={author} size="sm" />
                <div className={`flex-1 ${isMine ? 'text-right' : ''}`}>
                  <div className={`inline-block max-w-full px-3 py-2 rounded-2xl ${
                    isMine ? 'bg-[#D93025] text-white' : 'bg-zinc-100 text-zinc-900'
                  }`}>
                    <div className={`text-[11px] font-semibold mb-0.5 ${isMine ? 'text-white/80' : 'text-zinc-500'}`}>
                      {c.user_name}
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words text-left">{c.text}</div>
                  </div>
                  <div className={`flex items-center gap-2 mt-1 text-[10px] text-zinc-400 ${isMine ? 'justify-end' : ''}`}>
                    <span title={format(parseISO(c.created_at), 'd. MMM yyyy HH:mm', { locale: getLocale() })}>
                      {formatDistanceToNow(parseISO(c.created_at), { addSuffix: true, locale: getLocale() })}
                    </span>
                    {(isMine || user?.role === 'admin') && (
                      <button
                        type="button"
                        onClick={() => handleDelete(c.id)}
                        data-testid={`delete-comment-${c.id}`}
                        className="hover:text-red-500"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-zinc-100 p-3 flex items-end gap-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKey}
            placeholder={t('taskDetail.commentPlaceholder')}
            className="resize-none min-h-[42px] max-h-32"
            rows={1}
            data-testid="comment-input"
          />
          <Button
            onClick={handleSend}
            disabled={sending || !text.trim()}
            data-testid="send-comment-btn"
            className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full h-10 w-10 p-0 flex-shrink-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailDialog;
